<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { useLedgerStore } from '../stores/ledger';
import { formatNgonka, formatDateTime, shortId } from '../lib/format';
import type { LedgerType } from '../api/ledger';

const ledger = useLedgerStore();

const ALL_TYPES: { value: LedgerType; label: string }[] = [
  { value: 'charge', label: 'Charges' },
  { value: 'topup', label: 'Top-ups' },
  { value: 'refund', label: 'Refunds' },
  { value: 'trial', label: 'Trial' },
  { value: 'adjust', label: 'Adjustments' },
];

onMounted(() => ledger.reset());

const hasFilters = computed(() => ledger.types.length > 0);

function toggleType(t: LedgerType): void {
  const next = ledger.types.includes(t)
    ? ledger.types.filter((x) => x !== t)
    : [...ledger.types, t];
  ledger.setTypes(next);
}

function clearFilters(): void {
  ledger.setTypes([]);
}

function exportCsv(): void {
  const headers = [
    'created_at',
    'type',
    'amount_ngonka',
    'balance_after',
    'req_id',
    'model',
    'native_amount',
    'native_unit',
    'markup_pct',
    'gnk_usd_rate',
    'prompt_tokens',
    'completion_tokens',
    'total_tokens',
    'overdraft',
    'topup_source',
    'note',
  ];
  const escape = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = ledger.entries.map((e) =>
    [
      e.created_at,
      e.type,
      e.amount_ngonka,
      e.balance_after,
      e.req_id,
      e.model,
      e.native_amount,
      e.native_unit,
      e.markup_pct,
      e.gnk_usd_rate,
      e.prompt_tokens,
      e.completion_tokens,
      e.total_tokens,
      e.overdraft,
      e.topup_source,
      e.note,
    ]
      .map(escape)
      .join(','),
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `gonka-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
</script>

<template>
  <div class="max-w-6xl mx-auto px-6 py-8 flex flex-col gap-4">
    <div class="flex items-center justify-between flex-wrap gap-3">
      <h1 class="text-lg font-semibold">Ledger</h1>
      <button class="btn-ghost text-xs" :disabled="ledger.entries.length === 0" @click="exportCsv">
        Export CSV
      </button>
    </div>

    <div class="card p-4 flex items-center gap-2 flex-wrap">
      <span class="text-xs text-muted mr-1">Filter:</span>
      <button
        v-for="t in ALL_TYPES"
        :key="t.value"
        class="px-3 py-1 rounded-full text-xs border transition"
        :class="
          ledger.types.includes(t.value)
            ? 'bg-accent border-accent text-white'
            : 'bg-transparent border-border text-muted hover:text-ink'
        "
        @click="toggleType(t.value)"
      >
        {{ t.label }}
      </button>
      <button
        v-if="hasFilters"
        class="ml-auto text-xs text-muted hover:text-ink"
        @click="clearFilters"
      >
        Clear
      </button>
    </div>

    <div v-if="ledger.error" class="card p-3 border-danger text-danger text-sm">{{ ledger.error }}</div>

    <section class="card overflow-x-auto">
      <table class="w-full table min-w-[800px]">
        <thead>
          <tr>
            <th>When</th>
            <th>Type</th>
            <th>Amount (ngonka)</th>
            <th>Balance after</th>
            <th>Model</th>
            <th>Tokens</th>
            <th>Request</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in ledger.entries" :key="e.id">
            <td class="text-muted whitespace-nowrap">{{ formatDateTime(e.created_at) }}</td>
            <td>
              <span class="pill">{{ e.type }}</span>
              <span v-if="e.overdraft" class="pill ml-1" style="background:#3a1f1f;color:#f5a3a3;">overdraft</span>
            </td>
            <td
              :class="{
                'text-ok': !e.amount_ngonka.startsWith('-') && e.type !== 'charge',
                'text-danger': e.amount_ngonka.startsWith('-'),
              }"
            >
              {{ formatNgonka(e.amount_ngonka) }}
            </td>
            <td>{{ formatNgonka(e.balance_after) }}</td>
            <td class="text-muted">{{ e.model ?? '—' }}</td>
            <td class="text-muted text-xs">
              <span v-if="e.total_tokens != null">
                {{ e.prompt_tokens ?? '?' }} / {{ e.completion_tokens ?? '?' }} / {{ e.total_tokens }}
              </span>
              <span v-else>—</span>
            </td>
            <td class="text-muted">
              <code v-if="e.req_id">{{ shortId(e.req_id, 6, 4) }}</code>
              <span v-else>—</span>
            </td>
          </tr>
          <tr v-if="!ledger.loading && ledger.entries.length === 0">
            <td colspan="7" class="text-muted italic text-center py-6">No entries.</td>
          </tr>
        </tbody>
      </table>
    </section>

    <div class="flex justify-center">
      <button
        v-if="ledger.nextCursor"
        class="btn-ghost"
        :disabled="ledger.loading"
        @click="ledger.loadMore()"
      >
        {{ ledger.loading ? 'Loading…' : 'Load more' }}
      </button>
      <div v-else-if="ledger.entries.length > 0" class="text-muted text-xs py-2">
        End of ledger.
      </div>
    </div>
  </div>
</template>
